import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';
import { setToken } from '../../../utils/auth';
import { FiCheckCircle as CheckCircle, FiTarget as Target, FiChevronLeft as ChevronLeft, FiChevronRight as ChevronRight, FiChevronDown as ChevronDown, FiChevronUp as ChevronUp, FiX as X, FiClock as Clock, FiInfo as Info, FiUsers as Users, FiUserPlus as UserPlus, FiShare2 as Share, FiCheck as Check, FiTrendingUp as TrendingUp, FiBriefcase as Briefcase, FiCode as Code, FiLayers as Layers, FiTrash2 as Trash } from 'react-icons/fi';
import { getMissingSections, Question as AssessmentQuestion } from './utils/conditionalLogic';
import ErrorDisplay from '../../../components/ErrorDisplay';
import { assessmentAPI } from '../../../services/realApi';
import { mockUserAuth } from '../../../services/mockData';

// Hooks
import useAssessment, { AssessmentListItem } from './hooks/useAssessment';
import useAssessmentNavigation from './hooks/useAssessmentNavigation';
import useAssessmentContact from './hooks/useAssessmentContact';
import useAssessmentPDF from './hooks/useAssessmentPDF';
import useSMEBooking from './hooks/useSMEBooking';

// Components
import AssessmentResults from './components/AssessmentResults';
import SMEBookingModal from './components/SMEBookingModal';
import QuestionTooltip from './components/QuestionTooltip';
import ShareLinkModal from './components/ShareLinkModal';
import AssessmentOnboardingForm from './components/AssessmentOnboardingForm';

const AssessmentPage = () => {
  const { user, refreshAuth, refreshAuthSilently, setUserDirectly } = useAuth();
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check if this is onboarding mode (public assessment)
  const isOnboardingMode = ['/assessments/onboard', '/assessments/azure'].includes(location.pathname);

  // Derive cloud provider from URL path
  const cloudProvider = location.pathname === '/assessments/azure' ? 'Azure' : 'AWS';

  // Track if onboarding form was just submitted to prevent auto-logout
  // Use ref instead of state to avoid async state update issues
  const justCompletedOnboarding = useRef(false);

  // Track if we've already initialized the assessment from share link
  const hasInitializedFromShareLink = useRef(false);

  // Track when handleNewAssessment is driving navigation (skip reset effects)
  const isNewAssessmentNavigation = useRef(false);

  // Track if we've already attempted auto-login for this specific token
  const hasAttemptedAutoLogin = useRef(false);
  const lastShareToken = useRef<string | null>(null);

  // Token-based access state
  const shareToken = searchParams.get('token');
  const [anonymousSessionToken, setAnonymousSessionToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(false);

  // Share link modal state
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const [shareLinkData, setShareLinkData] = useState<{ share_url: string; days_until_expiry: number; expires_at: string } | null>(null);
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [shareLinkError, setShareLinkError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Timeline expand state
  const [expandedTimelineId, setExpandedTimelineId] = useState<string | null>(null);
  const [timelineResults, setTimelineResults] = useState<Record<string, Record<string, number>>>({});
  const [timelineResultsLoading, setTimelineResultsLoading] = useState<string | null>(null);

  // Category name lookup (mirrors AssessmentResults formatCategoryName)
  const formatCategoryName = (category: string): string => {
    const categoryNames: Record<string, string> = {
      'business_objectives': 'Business Strategy Alignment',
      'data_readiness': 'Data Foundation Maturity',
      'technical_infrastructure': 'Technical Infrastructure Readiness',
      'organizational_readiness': 'Organizational Capabilities',
      'governance_compliance': 'Governance & Compliance Readiness',
      'ai_ml_implementation': 'AI/ML Implementation Maturity',
      'ai_ml_strategy_governance': 'AI/ML Strategy & Governance',
      'data_foundation': 'Data Foundation',
      'security_compliance': 'Security & Compliance',
      'infrastructure_platform': 'Infrastructure & Platform',
      'cost_optimization_finops': 'Cost Optimization & FinOps',
      'observability_operations': 'Observability & Operations',
      'ai_model_operations': 'AI Model Operations',
      'agent_automation': 'Agent & Automation',
    };
    if (categoryNames[category]) return categoryNames[category];
    return category
      .replaceAll('_', ' ')
      .replaceAll(/\b\w/g, (l: string) => l.toUpperCase())
      .replaceAll(/\bAi\b/g, 'AI')
      .replaceAll(/\bMl\b/g, 'ML');
  };

  // Fetch and cache category scores for a timeline entry
  const fetchTimelineCategoryScores = async (assessmentId: string): Promise<Record<string, number> | null> => {
    if (timelineResults[assessmentId]) return timelineResults[assessmentId];
    setTimelineResultsLoading(assessmentId);
    try {
      const result = await assessmentAPI.getAssessmentResults(assessmentId);
      if (result.success) {
        const scores = result.data.categoryScores || result.data.category_scores || {};
        setTimelineResults(prev => ({ ...prev, [assessmentId]: scores }));
        return scores;
      }
    } catch (e: unknown) {
      console.error('Failed to fetch timeline results:', e);
    } finally {
      setTimelineResultsLoading(null);
    }
    return null;
  };

  // Reset the onboarding completion flag when path changes
  useEffect(() => {
    if (!isOnboardingMode) {
      justCompletedOnboarding.current = false;
    }
  }, [isOnboardingMode]);

  // Auto-logout if user is logged in and visits onboarding URL
  // BUT don't logout if they just completed the onboarding form
  useEffect(() => {

    if (isOnboardingMode && user && !justCompletedOnboarding.current) {
      // Clear tokens and user data (local logout, don't use logout() to avoid redirect)
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedClientId');
      // Refresh auth context to reflect logged-out state
      refreshAuth();
      // Ensure we stay on onboarding page (prevent redirect to /login)
      navigate(location.pathname, { replace: true });
    } else {
    }
  }, [isOnboardingMode, user, refreshAuth, navigate, location.pathname]);

  // Check if user can create assessments (AWS users cannot)
  const canCreateAssessment =
    hasPermission(PERMISSIONS.ASSESSMENTS_MANAGE) ||
    hasPermission(PERMISSIONS.ASSESSMENTS_VIEW);

  // Get assessment ID from URL for draft functionality
  const assessmentIdFromUrl = searchParams.get('assessment_id');

  // Debug URL parameters - use effect to prevent log spam on every render
  useEffect(() => {
  }, [location.pathname, location.search, assessmentIdFromUrl, searchParams]);

  // Onboarding form state
  const [onboardingData, setOnboardingData] = useState({
    email: '',
    name: '',
    company: ''
  });

  // Check if joining existing assessment (has assessment_id) vs creating new one
  const isJoiningAssessment = !!assessmentIdFromUrl;
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);
  const [onboardingSuccess, setOnboardingSuccess] = useState(false);
  const [onboardingSuccessMessage, setOnboardingSuccessMessage] = useState('');

  // Debug logging for assessment ID
  useEffect(() => {
  }, [assessmentIdFromUrl, shareToken, searchParams]);

  // Handle token-based access - auto-login with share token
  useEffect(() => {
    const autoLoginWithShareToken = async () => {
      // Check if we need to attempt auto-login
      if (!assessmentIdFromUrl || !shareToken) {
        return;
      }

      // Reset tracking flag if the share token changed
      if (shareToken !== lastShareToken.current) {
        hasAttemptedAutoLogin.current = false;
        lastShareToken.current = shareToken;
      }

      // Skip if we've already attempted auto-login for this token
      if (hasAttemptedAutoLogin.current) {
        return;
      }

      // Skip if we already have an authenticated session with a token
      const existingToken = localStorage.getItem('token');
      const existingUser = localStorage.getItem('user');
      const existingAnonymousSession = localStorage.getItem('anonymous_session');

      if (existingToken && (existingUser || existingAnonymousSession)) {
        hasAttemptedAutoLogin.current = true;
        return;
      }

      // Mark that we're attempting auto-login for this token
      hasAttemptedAutoLogin.current = true;

      setIsLoadingSession(true);
      setTokenError(null);

      try {
        const loginData = await assessmentAPI.startAnonymousSession(assessmentIdFromUrl, shareToken);


        // Store JWT token
        setToken(loginData.access_token);

        // Handle anonymous session vs regular user session
        if (loginData.is_anonymous) {
          // Anonymous company session - store minimal data
          localStorage.setItem('anonymous_session', JSON.stringify({
            is_anonymous: true,
            client_name: loginData.client_name,
            client_id: loginData.client_id,
            subscription_plan: loginData.subscription_plan,
            assessment_id: loginData.assessment_id,
            expires_at: loginData.expires_at
          }));

          // Don't store user data for anonymous sessions
          localStorage.removeItem('user');

          // Set user directly without triggering loading=true
          // This prevents AppContent from unmounting the route tree
          setUserDirectly({
            id: loginData.user_id || 'anonymous',
            is_anonymous: true,
            client_name: loginData.client_name,
            client_id: loginData.client_id,
            subscription_plan: loginData.subscription_plan,
            email: null,
            name: null,
            role: 'anonymous',
            permissions: mockUserAuth.permissions,
            aws_accessible_modules: mockUserAuth.aws_accessible_modules
          });
        } else {
          // Regular user session (assessment_only plan)

          // Remove anonymous session data if any
          localStorage.removeItem('anonymous_session');

          // Fetch fresh user data from /auth/me without triggering loading=true.
          // loginData.user may be undefined for assessment_only users, so we
          // always fetch from the API to get the full user object.
          await refreshAuthSilently();
        }

      } catch (error: unknown) {
        console.error('[AssessmentPage] Failed to auto-login:', error);
        setTokenError(error instanceof Error ? error.message : 'Auto-login failed');
        // Reset flag on error so user can retry
        hasAttemptedAutoLogin.current = false;
      } finally {
        setIsLoadingSession(false);
      }
    };

    autoLoginWithShareToken();
  }, [assessmentIdFromUrl, shareToken, setUserDirectly, refreshAuthSilently]);

  // Load anonymous session from localStorage on mount
  useEffect(() => {
    const storedSessionToken = localStorage.getItem('anonymous_session_token');
    const storedExpiresAt = localStorage.getItem('anonymous_session_expires_at');

    if (storedSessionToken && storedExpiresAt) {
      const expiresAt = new Date(storedExpiresAt);
      const now = new Date();

      // Check if session is still valid
      if (expiresAt > now) {
        setAnonymousSessionToken(storedSessionToken);
      } else {
        localStorage.removeItem('anonymous_session_token');
        localStorage.removeItem('anonymous_session_client_id');
        localStorage.removeItem('anonymous_session_expires_at');
      }
    }
  }, []);

  // Auto-save status indicator
  const [autoSaveStatus, setAutoSaveStatus] = useState<'' | 'saving' | 'saved'>('');

  // Share/Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', name: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteSuccessMessage, setInviteSuccessMessage] = useState('');

  // Assessment hooks
  const {
    questionnaire,
    fullQuestionnaire,
    currentStep,
    setCurrentStep,
    currentSectionIndex,
    setCurrentSectionIndex,
    currentSubsectionIndex,
    setCurrentSubsectionIndex,
    answers,
    setAnswers,
    assessmentResults,
    setAssessmentResults,
    loading,
    submittedAssessmentId,
    setSubmittedAssessmentId,
    assessmentsList,
    assessmentsLoading,
    initialLoading,
    error,
    setError,
    loadAssessments,
    calculateAssessment,
    checkAssessmentResults,
    submitAssessment,
    createDraftAssessment,
    userPersona,
    handleSetPersona,
  } = useAssessment({ cloudProvider: isOnboardingMode ? cloudProvider : undefined });

  const {
    getCurrentSubsection,
    getCurrentSubsectionGlobalIndex,
    getTotalSubsections,
    goToNextSubsection,
    goToPreviousSubsection,
    isLastSubsection,
    isFirstSubsection,
    filteredQuestionnaire
  } = useAssessmentNavigation(questionnaire, currentSectionIndex, currentSubsectionIndex, answers);

  const {
    contactSubmitted,
    contactSubmitting,
    showContactModal,
    setShowContactModal,
    handleContactSubmission
  } = useAssessmentContact();

  const {
    pdfDownloading,
    handlePDFDownload
  } = useAssessmentPDF();

  const {
    showSMEBooking,
    setShowSMEBooking,
    selectedTimeSlot,
    setSelectedTimeSlot,
    bookingLoading,
    generateTimeSlots,
    bookSMESession
  } = useSMEBooking();

  const [currentAssessmentIdFromResults, setCurrentAssessmentIdFromResults] = useState<string | null>(null);

  // Auto-poll for results when in processing step
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    if (currentStep === 'processing' && submittedAssessmentId) {
      interval = setInterval(async () => {
        try {
          await checkAssessmentResults(submittedAssessmentId);
        } catch (err: unknown) {
          console.error('Error checking results:', err);
        }
      }, 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentStep, submittedAssessmentId, checkAssessmentResults]);

  // Unified effect: handle assessment from URL for all user states
  useEffect(() => {
    const handleAssessmentFromUrl = async () => {

      if (!assessmentIdFromUrl) {
        return;
      }

      // Wait for auto-login to complete
      if (isLoadingSession) {
        return;
      }

      // If there's a share token but no token in localStorage yet, wait for auto-login
      // to complete and store a JWT. If a token already exists (existing session),
      // proceed — the API call uses the token from localStorage directly.
      if (!user && shareToken) {
        const existingToken = localStorage.getItem('token');
        if (!existingToken) {
          return;
        }
      }

      // Skip if already showing content (questionnaire, processing, results)
      if (currentStep !== 'assessments') {
        return;
      }

      // Skip if already initialized from this share link
      if (hasInitializedFromShareLink.current) {
        return;
      }

      // In onboarding mode, wait for session to be created
      if (isOnboardingMode && !user && !anonymousSessionToken) {
        return;
      }

      hasInitializedFromShareLink.current = true;

      try {
        const draft = await assessmentAPI.loadDraft(assessmentIdFromUrl);
        setSubmittedAssessmentId(assessmentIdFromUrl);

        if (draft.isDraft && draft.status === 'draft') {
          if (draft.responses && Object.keys(draft.responses).length > 0) {
            setAnswers(draft.responses);
            // Restore persona from saved answers (but NOT for share-link sessions)
            if (draft.responses.user_persona && !shareToken) {
              handleSetPersona(draft.responses.user_persona);
              setCurrentStep('questionnaire');
            } else {
              // No persona saved or share-link session — go to persona selection
              setCurrentStep('persona-selection');
            }
          } else {
            setCurrentStep('persona-selection');
          }
        } else if (draft.status === 'processing') {
          setCurrentStep('processing');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (draft.status === 'completed') {
          await checkAssessmentResults(assessmentIdFromUrl);
        } else if (draft.status === 'failed') {
          setError('Assessment processing failed. Please try again.');
          setCurrentStep('questionnaire');
          if (draft.responses && Object.keys(draft.responses).length > 0) {
            setAnswers(draft.responses);
          }
        } else {
          setCurrentStep('questionnaire');
        }
      } catch (error: unknown) {
        console.error('[AssessmentPage] Failed to load assessment:', error);

        // On 401 with a share token available, clear stale session and let auto-login retry
        if ((error as { status?: number }).status === 401 && shareToken) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('anonymous_session');
          hasInitializedFromShareLink.current = false;
          hasAttemptedAutoLogin.current = false;
          setUserDirectly(null);
          return;
        }

        setCurrentStep('questionnaire');
      }
    };

    handleAssessmentFromUrl();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentIdFromUrl, anonymousSessionToken, isOnboardingMode, user, isLoadingSession, shareToken, setUserDirectly]);

  // Reset share link initialization when URL params change (e.g., clicking a second share link)
  useEffect(() => {
    if (isNewAssessmentNavigation.current) return;
    hasInitializedFromShareLink.current = false;
  }, [assessmentIdFromUrl, shareToken]);

  // Reset assessment state when navigating to a different assessment ID
  const prevAssessmentId = useRef(assessmentIdFromUrl);
  useEffect(() => {
    if (isNewAssessmentNavigation.current) {
      // handleNewAssessment is driving this navigation, just update the ref
      prevAssessmentId.current = assessmentIdFromUrl;
      isNewAssessmentNavigation.current = false;
      return;
    }
    if (assessmentIdFromUrl && assessmentIdFromUrl !== prevAssessmentId.current) {
      prevAssessmentId.current = assessmentIdFromUrl;
      setCurrentStep('assessments');
      setAnswers({});
      setAssessmentResults(null);
      hasInitializedFromShareLink.current = false;
    }
  }, [assessmentIdFromUrl, setCurrentStep, setAnswers, setAssessmentResults]);

  // Auto-save draft with 2-second debounce
  useEffect(() => {
    if (!assessmentIdFromUrl || currentStep !== 'questionnaire') return;

    const timeoutId = setTimeout(async () => {
      if (Object.keys(answers).length > 0) {
        setAutoSaveStatus('saving');
        try {
          // For share-link sessions, exclude user_persona from auto-save
          const answersToSave = shareToken
            ? Object.fromEntries(Object.entries(answers).filter(([key]) => key !== 'user_persona'))
            : answers;
          await assessmentAPI.saveDraft(assessmentIdFromUrl, answersToSave);
          setAutoSaveStatus('saved');
          setTimeout(() => setAutoSaveStatus(''), 2000); // Clear "Saved" indicator after 2 seconds
        } catch (err: unknown) {
          console.error('Auto-save failed:', err);
          setAutoSaveStatus('');
        }
      }
    }, 2000); // 2 second debounce

    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, assessmentIdFromUrl, currentStep]);

  // Auto-poll assessments list when on the timeline and there are processing entries
  const hasProcessingAssessments = assessmentsList.some(a => a.status === 'processing');
  const loadAssessmentsRef = useRef(loadAssessments);
  loadAssessmentsRef.current = loadAssessments;
  useEffect(() => {
    if (currentStep !== 'assessments' || !hasProcessingAssessments) return;
    const intervalId = setInterval(() => {
      loadAssessmentsRef.current();
    }, 15000); // poll every 15 seconds
    return () => clearInterval(intervalId);
  }, [currentStep, hasProcessingAssessments]);

  const handleAnswerChange = (questionId: string, answer: unknown) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNext = () => {
    goToNextSubsection(setCurrentSectionIndex, setCurrentSubsectionIndex);
    // Scroll to top of page when moving to next question
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    goToPreviousSubsection(setCurrentSectionIndex, setCurrentSubsectionIndex);
    // Scroll to top of page when moving to previous question
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    try {
      // Pass assessment_id from URL if present (for onboarding flow)
      const result = await submitAssessment(assessmentIdFromUrl);
      if (result.success) {
        setCurrentStep('processing');
        // Scroll to top when showing processing screen
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err: unknown) {
      console.error('Error submitting assessment:', err);
    }
  };

  const handleBookSME = () => {
    setShowContactModal(true);
  };

  const handleSMESubmit = async (contactData: Record<string, unknown>) => {
    try {
      await bookSMESession(contactData);
    } catch (err: unknown) {
      console.error('Error booking SME session:', err);
    }
  };

  const handleOnboardingSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setOnboardingLoading(true);
    setOnboardingError(null);

    try {
      let response;

      if (isJoiningAssessment) {
        // Joining existing assessment via shared link
        response = await assessmentAPI.joinAssessment(assessmentIdFromUrl, {
          email: onboardingData.email,
          name: onboardingData.name
        });
      } else {
        // Creating new company and assessment
        response = await assessmentAPI.onboardAssessment({ ...onboardingData, cloud_provider: cloudProvider });
      }

      // Check if user needs approval
      if (response.pendingApproval) {
        // User needs approval - show success message and wait for approval
        setOnboardingError(null);
        setOnboardingSuccess(true);
        setOnboardingSuccessMessage(`Thank you for your interest in joining ${response.companyName}! Your request has been sent to existing team members for approval. You'll receive an email once your access is approved.`);
        setOnboardingLoading(false);
        return;
      }

      // Store JWT token and basic user info (only if not pending approval)
      setToken(response.token);
      localStorage.setItem('user', JSON.stringify({
        id: response.userId,
        email: onboardingData.email,
        name: onboardingData.name,
        role: 'client',
        clientId: response.clientId
      }));

      // CRITICAL: Set flag BEFORE any state updates to prevent auto-logout effect
      justCompletedOnboarding.current = true;

      // Update AuthContext with the new user
      await refreshAuth();

      // Navigate to assessment page
      const targetAssessmentId = isJoiningAssessment ? assessmentIdFromUrl : response.assessmentId;
      navigate(`/hub/assessments?assessment_id=${targetAssessmentId}`, { replace: true });
    } catch (err: unknown) {
      console.error('Onboarding failed:', err);
      setOnboardingError(err instanceof Error ? err.message : 'Failed to start assessment. Please try again.');
      setOnboardingLoading(false);
    }
  };

  const handleOnboardingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setOnboardingData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInviteSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setInviteError(null);
    setInviteLoading(true);

    try {
      const response = await assessmentAPI.inviteToAssessment(assessmentIdFromUrl!, inviteData);

      if (response.success && response.data) {
        setInviteSuccessMessage(response.data.message || 'Invitation sent successfully');
        setInviteSuccess(true);
        setTimeout(() => {
          setShowInviteModal(false);
          setInviteData({ email: '', name: '' });
          setInviteSuccess(false);
          setInviteSuccessMessage('');
        }, 3000);
      } else {
        setInviteError(response.error || 'Failed to invite user');
      }
    } catch (error: unknown) {
      console.error('Error inviting user:', error);
      setInviteError(error instanceof Error ? error.message : 'Failed to invite user');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleGenerateShareLink = async () => {
    const resolvedAssessmentId = assessmentIdFromUrl || assessmentResults?.assessment_id || assessmentResults?.id || currentAssessmentIdFromResults || submittedAssessmentId;
    if (!resolvedAssessmentId) {
      setShareLinkError('No assessment ID found');
      return;
    }

    setShareLinkLoading(true);
    setShareLinkError(null);

    try {
      const response = await assessmentAPI.generateShareLink(resolvedAssessmentId);

      if (response.success && response.data) {
        setShareLinkData(response.data);
        setShowShareLinkModal(true);
      } else {
        setShareLinkError(response.error || 'Failed to generate share link');
      }
    } catch (error: unknown) {
      console.error('Error generating share link:', error);
      setShareLinkError(error instanceof Error ? error.message : 'Failed to generate share link');
    } finally {
      setShareLinkLoading(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareLinkData?.share_url) {
      return;
    }

    try {
      await navigator.clipboard.writeText(shareLinkData.share_url);
      setLinkCopied(true);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    } catch (err: unknown) {
      console.error('Failed to copy to clipboard:', err);
      setShareLinkError('Failed to copy to clipboard');
    }
  };

  const handleBackToAssessments = async () => {
    // Refresh assessments list before going back
    await loadAssessments();
    setCurrentStep('assessments');
  };

  const handleNewAssessment = async () => {
    // Create a draft assessment first
    const result = await createDraftAssessment();

    if (result.success) {
      const assessmentId = result.data.id;

      // Signal that this is a new assessment navigation — skip reset effects
      isNewAssessmentNavigation.current = true;
      hasInitializedFromShareLink.current = true;

      // Update URL with assessment_id
      navigate(`/hub/assessments?assessment_id=${assessmentId}`, { replace: true });

      // Reset state and go to persona selection
      setCurrentSectionIndex(0);
      setCurrentSubsectionIndex(0);
      setAssessmentResults(null);
      setSubmittedAssessmentId(null);

      // Pre-fill answers from previous assessment if available
      if (result.prefillAnswers && Object.keys(result.prefillAnswers).length > 0) {
        setAnswers(result.prefillAnswers);
      } else {
        setAnswers({});
      }

      // Go to persona selection screen
      setCurrentStep('persona-selection');
    } else {
      console.error('[AssessmentPage] Failed to create draft:', result.error);
      // Show error but still allow questionnaire access
      setCurrentStep('persona-selection');
    }
  };

  // Calculate completion percentage
  const getCompletionPercentage = () => {
    if (!questionnaire || questionnaire.length === 0) return 0;

    const allQuestions: AssessmentQuestion[] = [];
    (questionnaire || []).forEach(section => {
      (section.sections || []).forEach(sub => {
        allQuestions.push(...(sub.questions || []));
      });
    });

    const totalQuestions = allQuestions.length;
    const answeredQuestions = allQuestions.filter(question => {
      const answer = answers[question.id];
      if (answer == null || answer === '') return false;
      if (Array.isArray(answer)) return answer.length > 0;
      return true;
    }).length;

    return totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;
  };

  // Handle assessment submission with completion check
  const handleSubmitClick = () => {
    const completionPercentage = getCompletionPercentage();

    // Check for missing persona sections (single-persona users)
    if (userPersona && userPersona !== 'both' && fullQuestionnaire) {
      const otherPersona = userPersona === 'business' ? 'technical' : 'business';
      const missing = getMissingSections(fullQuestionnaire, otherPersona, answers);
      if (missing.length > 0) {
        setCurrentStep('missing-sections');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    if (completionPercentage < 75) {
      setCurrentStep('incomplete-warning');
      // Scroll to top when showing incomplete warning
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Call the appropriate submit function based on context
      if (assessmentIdFromUrl) {
        handleSubmit();
      } else {
        calculateAssessment();
      }
    }
  };


  const renderQuestion = (question: AssessmentQuestion): React.ReactNode => {
    const questionId = question.id;
    const currentValue = (answers[questionId] as string | string[] | undefined) || '';

    switch (question.type) {
      case 'textarea':
        return (
          <textarea
            value={currentValue}
            onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            placeholder="Please provide your answer..."
          />
        );

      case 'yesno':
        return (
          <div className="flex space-x-4">
            {['Yes', 'No'].map((option) => (
              <label key={option} className="flex items-center">
                <input
                  type="radio"
                  name={questionId}
                  value={option}
                  checked={currentValue === option}
                  onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 transition-colors">
                  {option}
                </span>
              </label>
            ))}
          </div>
        );

      case 'yesno_with_text':
        return (
          <div className="space-y-3">
            <div className="flex space-x-4">
              {['Yes', 'No'].map((option) => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name={questionId}
                    value={option}
                    checked={(typeof currentValue === 'string' ? currentValue === option : Array.isArray(currentValue) && currentValue.includes(option))}
                    onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 transition-colors">
                    {option}
                  </span>
                </label>
              ))}
            </div>
            {(currentValue === 'Yes' || (typeof currentValue === 'string' && currentValue.includes('Yes'))) && (
              <textarea
                placeholder="Please provide additional details..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                onChange={(e) => handleAnswerChange(questionId + '_text', e.target.value)}
              />
            )}
          </div>
        );

      case 'yesno_with_values':
        return (
          <div className="space-y-3">
            <div className="flex space-x-4">
              {['Yes', 'No'].map((option) => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name={questionId}
                    value={option}
                    checked={(typeof currentValue === 'string' ? currentValue === option : Array.isArray(currentValue) && currentValue.includes(option))}
                    onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 transition-colors">
                    {option}
                  </span>
                </label>
              ))}
            </div>
            {(currentValue === 'Yes' || (typeof currentValue === 'string' && currentValue.includes('Yes'))) && (
              <input
                type="text"
                placeholder="Please specify values (e.g., RTO: 4 hours, RPO: 1 hour)"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                onChange={(e) => handleAnswerChange(questionId + '_values', e.target.value)}
              />
            )}
          </div>
        );

      case 'single':
        return (
          <div className="space-y-2">
            {(question.options || []).map((option) => (
              <label key={option} className="flex items-center">
                <input
                  type="radio"
                  name={questionId}
                  value={option}
                  checked={currentValue === option}
                  onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 transition-colors">
                  {option}
                </span>
              </label>
            ))}
          </div>
        );

      case 'select':
        return (
          <select
            value={currentValue as string}
            onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            <option value="">Select an option...</option>
            {(question.options || []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'multiple':
        return (
          <div className="space-y-2">
            {(question.options || []).map((option) => (
              <label key={option} className="flex items-center">
                <input
                  type="checkbox"
                  checked={Array.isArray(currentValue) && currentValue.includes(option)}
                  onChange={() => {
                    const current: string[] = Array.isArray(currentValue) ? currentValue : [];
                    const newValue = current.includes(option)
                      ? current.filter((item: string) => item !== option)
                      : [...current, option];
                    handleAnswerChange(questionId, newValue);
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 transition-colors">
                  {option}
                </span>
              </label>
            ))}
          </div>
        );

      case 'percentage':
        return (
          <select
            value={currentValue as string}
            onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            <option value="">Select percentage...</option>
            {['0%', '25%', '50%', '75%', '100%'].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            value={currentValue as string}
            onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            placeholder="Please provide your answer..."
          />
        );
    }
  };

  // Contact form state for anonymous users
  const [contactEmail, setContactEmail] = useState('');
  const isAnonymousUser = user?.role === 'anonymous';

  const handleContactSubmit = async () => {
    if (isAnonymousUser && !contactEmail) return;
    try {
      const contactData = {
        ...(isAnonymousUser && { email: contactEmail }),
        prospectId: assessmentIdFromUrl || submittedAssessmentId || currentAssessmentIdFromResults || assessmentResults?.id || '',
        responses: answers,
        assessmentResults: assessmentResults,
        description: 'I would like to schedule a consultation to review my AI strategy assessment results and discuss next steps.'
      };
      await handleContactSubmission(contactData);
    } catch (err: unknown) {
      console.error('Error submitting contact:', err);
    }
  };


  // Show onboarding form for unauthenticated users in onboarding mode
  // BUT skip the form if they have an anonymous session (share token access)
  if (isOnboardingMode && !user && !anonymousSessionToken) {
    return (
      <AssessmentOnboardingForm
        isJoiningAssessment={isJoiningAssessment}
        onboardingSuccess={onboardingSuccess}
        onboardingSuccessMessage={onboardingSuccessMessage}
        onboardingData={onboardingData}
        onboardingError={onboardingError}
        onboardingLoading={onboardingLoading}
        handleOnboardingSubmit={handleOnboardingSubmit}
        handleOnboardingChange={handleOnboardingChange}
      />
    );
  }

  // Show expired token error if token validation failed
  if (tokenError && shareToken) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8 transition-colors">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center transition-colors">
                <Clock className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
            </div>

            {/* Main Message */}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center transition-colors">
              Assessment Link Expired
            </h2>

            <div className="space-y-4 text-gray-700 dark:text-gray-300 mb-6 transition-colors">
              <p className="text-center">
                {tokenError}
              </p>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 transition-colors">
                <div className="flex items-start space-x-2">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    {user ? (
                      <p>Assessment share links expire after 30 days for security purposes. You can generate a new link from the assessment page.</p>
                    ) : (
                      <p>Assessment share links expire after 30 days for security purposes. Please contact the organization that shared this link with you to request a new one.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {user && assessmentIdFromUrl && (
                <button
                  onClick={async () => {
                    setTokenError(null);
                    try {
                      const response = await assessmentAPI.regenerateShareLink(assessmentIdFromUrl);
                      if (response.success && response.data) {
                        // Update URL with new token
                        globalThis.location.href = response.data.share_url;
                      }
                    } catch (err: unknown) {
                      console.error('Failed to regenerate link:', err);
                      setTokenError('Failed to generate new link. Please try again.');
                    }
                  }}
                  className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <Share className="w-5 h-5" />
                  <span>Generate New Link</span>
                </button>
              )}

              <button
                onClick={() => {
                  if (user) {
                    navigate('/hub/assessments');
                  } else {
                    navigate('/');
                  }
                }}
                className="w-full px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {user ? 'Go to My Assessments' : 'Go to Home'}
              </button>
            </div>

            {!user && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-center text-gray-500 dark:text-gray-400">
                  Need help? Contact the person or organization that shared this assessment link with you.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while creating anonymous session
  if (isLoadingSession && shareToken) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Validating access...</p>
        </div>
      </div>
    );
  }

  // Show loading state while determining initial view
  if (initialLoading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading assessments...</p>
        </div>
      </div>
    );
  }

  // Show error state for critical errors
  if (error && currentStep === 'questionnaire') {
    return (
      <ErrorDisplay
        error={error}
        title="Error Loading Assessment"
        onRetry={() => {
          setError(null);
          setCurrentStep('assessments');
          globalThis.location.reload();
        }}
      />
    );
  }

  // Show loading state while questionnaire is being loaded
  if (!questionnaire) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading assessment...</p>
        </div>
      </div>
    );
  }

  // Persona selection step
  if (currentStep === 'persona-selection') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
          <div className="text-center mb-8 sm:mb-10">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors">
              Select Your Role
            </h1>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 transition-colors max-w-2xl mx-auto">
              We'll tailor the assessment questions to your expertise. You can always expand to all questions later.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {/* Business Persona */}
            <button
              onClick={() => {
                handleSetPersona('business');
                setCurrentStep('questionnaire');
                setCurrentSectionIndex(0);
                setCurrentSubsectionIndex(0);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 sm:p-8 text-left hover:ring-2 hover:ring-blue-500 hover:shadow-xl transition-all group flex flex-col"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-4 sm:mb-5 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                <Briefcase className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 transition-colors">Business</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 transition-colors flex-1">
                Strategy, governance, cost management, and program alignment questions.
              </p>
              <div className="text-xs text-gray-500 dark:text-gray-500">
                CEO, COO, CFO, Business Development, Program Director
              </div>
            </button>

            {/* Technical Persona */}
            <button
              onClick={() => {
                handleSetPersona('technical');
                setCurrentStep('questionnaire');
                setCurrentSectionIndex(0);
                setCurrentSubsectionIndex(0);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 sm:p-8 text-left hover:ring-2 hover:ring-purple-500 hover:shadow-xl transition-all group flex flex-col"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mb-4 sm:mb-5 group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors">
                <Code className="w-6 h-6 sm:w-7 sm:h-7 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 transition-colors">Technical</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 transition-colors flex-1">
                Infrastructure, security, automation, model implementation, and tooling questions.
              </p>
              <div className="text-xs text-gray-500 dark:text-gray-500">
                Cloud Architect, DevOps Engineer, ML Engineer, Security Lead
              </div>
            </button>

            {/* Both Persona */}
            <button
              onClick={() => {
                handleSetPersona('both');
                setCurrentStep('questionnaire');
                setCurrentSectionIndex(0);
                setCurrentSubsectionIndex(0);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 sm:p-8 text-left hover:ring-2 hover:ring-green-500 hover:shadow-xl transition-all group flex flex-col"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center mb-4 sm:mb-5 group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
                <Layers className="w-6 h-6 sm:w-7 sm:h-7 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 transition-colors">Both</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 transition-colors flex-1">
                Answer all business and technical questions for the most comprehensive assessment.
              </p>
              <div className="text-xs text-gray-500 dark:text-gray-500">
                CTO, Founders, Technical Directors, Full-stack Leaders
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Questionnaire step - Prevent AWS users from accessing
  if (currentStep === 'questionnaire') {
    // Redirect AWS users back to assessments list
    // BUT allow anonymous users with share token access
    if (!canCreateAssessment && !anonymousSessionToken && !user?.is_anonymous) {
      return (
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">Your Assessments</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
              View your AI strategy assessments and their current status
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              You do not have permission to create new assessments. You can only view existing assessments.
            </p>
            <button
              onClick={handleBackToAssessments}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Assessments
            </button>
          </div>
        </div>
      );
    }
    const currentSubsection = getCurrentSubsection();
    const totalSubsections = getTotalSubsections();
    const currentGlobalIndex = getCurrentSubsectionGlobalIndex();

    if (!currentSubsection) {
      return <div>Error loading assessment</div>;
    }

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-4 sm:py-8 transition-colors">
        <div className="mx-auto px-3 sm:px-6 lg:px-8">
          {/* Header with Back Button */}
          <div className="mb-6">
            <button
              onClick={handleBackToAssessments}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              title="Back to Assessments"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Assessments</span>
            </button>
          </div>
        </div>

        <div className="mx-auto px-3 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white transition-colors">AI Strategy Assessment</h1>
                <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400 transition-colors truncate">
                  {questionnaire[currentSectionIndex].title}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleGenerateShareLink}
                  disabled={shareLinkLoading}
                  className="flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-green-600 text-white text-sm sm:text-base font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
                  title="Generate shareable link"
                >
                  <Share className="w-4 sm:w-5 h-4 sm:h-5" />
                  <span>Share</span>
                </button>
                {autoSaveStatus && (
                  <div className={`text-xs transition-colors ${
                    autoSaveStatus === 'saving' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'
                  }`}>
                    {autoSaveStatus === 'saving' ? 'Saving...' : <><Check className="inline w-3 h-3 mr-1" />Saved</>}
                  </div>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2 transition-colors">
                <span>Progress</span>
                <span>{currentGlobalIndex + 1} of {totalSubsections}</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 transition-colors">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentGlobalIndex + 1) / totalSubsections) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Current Subsection */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6 transition-colors">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6 transition-colors">
              {currentSubsection.title}
            </h2>

            <div className="space-y-6 sm:space-y-8">
              {(currentSubsection.questions || []).filter(q => !(q.id === 'cloud_provider' && answers.cloud_provider)).map((question) => (
                <div key={question.id}>
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 transition-colors">
                    <span className="inline-flex items-center">
                      {question.question}
                      {question.description && <QuestionTooltip description={question.description} />}
                    </span>
                  </span>
                  {renderQuestion(question)}
                </div>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-6 sm:mt-8 flex justify-between items-center">
            <button
              onClick={handlePrevious}
              disabled={isFirstSubsection()}
              className="px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 transition-colors text-sm sm:text-base"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            {/* Desktop progress dots */}
            <div className="hidden sm:flex space-x-2">
              {filteredQuestionnaire?.map((section, sectionIndex) =>
                section.sections.map((subsec, subsectionIndex) => {
                  const globalIndex = filteredQuestionnaire.slice(0, sectionIndex).reduce((acc, s) => acc + s.sections.length, 0) + subsectionIndex;
                  const allAnswered = (subsec.questions || []).every(q => {
                    const a = answers[q.id];
                    if (a == null || a === '') return false;
                    if (Array.isArray(a) && a.length === 0) return false;
                    return true;
                  });
                  let dotClass: string;
                  if (globalIndex === currentGlobalIndex) {
                    dotClass = 'bg-blue-600';
                  } else if (allAnswered) {
                    dotClass = 'bg-green-500';
                  } else {
                    dotClass = 'bg-gray-300 dark:bg-gray-600';
                  }
                  return (
                    <div key={`${sectionIndex}-${subsectionIndex}`} className="relative group">
                      <button
                        onClick={() => {
                          setCurrentSectionIndex(sectionIndex);
                          setCurrentSubsectionIndex(subsectionIndex);
                        }}
                        className={`w-3 h-3 rounded-full transition-colors cursor-pointer hover:scale-125 ${dotClass}`}
                      />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                        {subsec.title}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Mobile progress text */}
            <div className="sm:hidden text-xs text-gray-500 dark:text-gray-400 text-center">
              {currentGlobalIndex + 1} of {totalSubsections}
            </div>

            {isLastSubsection() ? (
              <button
                onClick={handleSubmitClick}
                disabled={loading || Object.keys(answers).length === 0}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 text-sm sm:text-base"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Analyzing...</span>
                  </>
                ) : (
                  <>
                    <span>Complete Assessment</span>
                    <Target className="w-4 h-4" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-1 sm:space-x-2 text-sm sm:text-base"
              >
                <span>Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Share Link Modal */}
        <ShareLinkModal
          show={showShareLinkModal}
          shareLinkData={shareLinkData}
          shareLinkError={shareLinkError}
          linkCopied={linkCopied}
          onClose={() => { setShowShareLinkModal(false); setShareLinkData(null); setShareLinkError(null); }}
          onCopy={handleCopyShareLink}
        />

        {/* Invite Team Member Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 transition-colors">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white transition-colors">Invite Team Member</h2>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteData({ email: '', name: '' });
                    setInviteError(null);
                    setInviteSuccess(false);
                    setInviteSuccessMessage('');
                  }}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                {inviteSuccess ? (
                  <div className="text-center">
                    <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 transition-colors">Success!</h3>
                    <p className="text-gray-600 dark:text-gray-400 transition-colors">
                      {inviteSuccessMessage}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleInviteSubmit} className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors mb-4">
                      Invite a colleague to collaborate on this assessment
                    </p>

                    {inviteError && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                        <p className="text-sm text-red-600 dark:text-red-400">{inviteError}</p>
                      </div>
                    )}

                    <div>
                      <label htmlFor="invite-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name
                      </label>
                      <input
                        id="invite-name"
                        type="text"
                        value={inviteData.name}
                        onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        placeholder="John Doe"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email
                      </label>
                      <input
                        id="invite-email"
                        type="email"
                        value={inviteData.email}
                        onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        placeholder="john@example.com"
                        required
                      />
                    </div>

                    <div className="flex space-x-3 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowInviteModal(false);
                          setInviteData({ email: '', name: '' });
                          setInviteError(null);
                          setInviteSuccessMessage('');
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        disabled={inviteLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={inviteLoading}
                      >
                        {inviteLoading ? 'Sending...' : 'Send Invitation'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Incomplete warning step
  if (currentStep === 'incomplete-warning') {
    const isSinglePersona = userPersona && userPersona !== 'both';
    const otherPersonaLabel = userPersona === 'business' ? 'Technical' : 'Business';

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8 transition-colors">
            {/* Warning Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 18.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>

            {/* Main Message */}
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 text-center transition-colors">
              Your Input Drives Better Results
            </h2>

            <div className="space-y-4 text-gray-700 dark:text-gray-300 mb-8 transition-colors">
              <p className="text-lg text-center">
                We understand this takes some time, but the quality of your assessment depends on the effort you invest.
              </p>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 transition-colors">
                <p className="text-center font-medium text-gray-900 dark:text-white">
                  This isn't just a survey — it's an AI-powered analysis that generates personalized recommendations based on the information you provide.
                </p>
              </div>

              <p className="text-center text-gray-600 dark:text-gray-400">
                The more complete your responses, the more accurate and actionable your AI strategy roadmap will be. Think of it as investing a few minutes now to save months of trial and error later.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => setCurrentStep('questionnaire')}
                className="w-full px-8 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 shadow-md"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Go Back & Complete</span>
              </button>

              {isSinglePersona && (
                <button
                  onClick={() => {
                    handleGenerateShareLink();
                    setCurrentStep('questionnaire');
                  }}
                  className="w-full px-8 py-3 border-2 border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 font-medium rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-center space-x-2"
                >
                  <Share className="w-5 h-5" />
                  <span>Share with a {otherPersonaLabel} Colleague</span>
                </button>
              )}

              <button
                onClick={() => {
                  if (assessmentIdFromUrl) {
                    handleSubmit();
                  } else {
                    calculateAssessment();
                  }
                }}
                disabled={loading}
                className="w-full px-8 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-700 dark:border-gray-300 border-t-transparent rounded-full animate-spin inline-block mr-2" />
                    Analyzing...
                  </>
                ) : (
                  'Submit Anyway'
                )}
              </button>
            </div>

            {/* Additional Context */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-center text-gray-500 dark:text-gray-400">
                Tip: Focus on the questions most relevant to your organization. Even partial answers help our AI understand your unique situation.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Missing sections step - shown when single-persona user tries to submit
  if (currentStep === 'missing-sections') {
    const otherPersona = userPersona === 'business' ? 'technical' : 'business';
    const otherPersonaLabel = otherPersona === 'business' ? 'Business' : 'Technical';
    const missing = fullQuestionnaire ? getMissingSections(fullQuestionnaire, otherPersona, answers) : [];

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8 transition-colors">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center transition-colors">
                <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center transition-colors">
              {otherPersonaLabel} Sections Not Completed
            </h2>

            <p className="text-gray-600 dark:text-gray-400 text-center mb-6 transition-colors">
              You answered the {userPersona} questions. There are {missing.length} {otherPersonaLabel.toLowerCase()} section{missing.length === 1 ? '' : 's'} with unanswered questions. For the most accurate assessment, consider completing them too.
            </p>

            {missing.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Missing sections:</p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  {missing.map((m) => (
                    <li key={m.subsectionTitle}>{m.subsectionTitle}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-3">
              {/* Option 1: Share with a colleague */}
              <button
                onClick={() => {
                  handleGenerateShareLink();
                  setCurrentStep('questionnaire');
                }}
                className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
              >
                <Share className="w-5 h-5" />
                <span>Share with a {otherPersonaLabel} Colleague</span>
              </button>

              {/* Option 2: Fill in myself */}
              <button
                onClick={() => {
                  handleSetPersona('both');
                  setCurrentStep('questionnaire');
                  // Navigate to first missing section if possible
                  if (missing.length > 0 && questionnaire) {
                    // Find the section/subsection index for the first missing subsection
                    // after persona is expanded to 'both'
                    setCurrentSectionIndex(0);
                    setCurrentSubsectionIndex(0);
                  }
                }}
                className="w-full px-6 py-3 border-2 border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 font-medium rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-center space-x-2"
              >
                <UserPlus className="w-5 h-5" />
                <span>Fill in Myself</span>
              </button>

              {/* Option 3: Submit as-is */}
              <button
                onClick={() => {
                  const completionPercentage = getCompletionPercentage();
                  if (completionPercentage < 75) {
                    setCurrentStep('incomplete-warning');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else if (assessmentIdFromUrl) {
                    handleSubmit();
                  } else {
                    calculateAssessment();
                  }
                }}
                disabled={loading}
                className="w-full px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit Anyway'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Processing step - waiting for results
  if (currentStep === 'processing') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header with Back Button */}
          <div className="mb-8">
            <button
              onClick={handleBackToAssessments}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              title="Back to Assessments"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Assessments</span>
            </button>
          </div>

          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mb-4 transition-colors">
                <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin transition-colors" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 transition-colors">
                Processing Your Assessment
              </h1>
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 transition-colors">
                Our AI agent powered by Amazon Bedrock is carefully analyzing your responses to create personalized recommendations tailored to your organization.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4 transition-colors">
                <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors">
                  <strong>This typically takes 3-5 minutes.</strong> You can safely close this page and we'll send you an email at <strong>{user?.email}</strong> when your results are ready.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Animated Header */}
              <div className="flex items-center justify-center space-x-4 mb-8">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce"></div>
                  <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 transition-colors">
                What we're analyzing:
              </h3>

              {/* Individual Cards for Each Analysis Item */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-900 dark:text-white font-medium text-left">Business strategy alignment</span>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-900 dark:text-white font-medium text-left">Data foundation maturity</span>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-900 dark:text-white font-medium text-left">Technical infrastructure readiness</span>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-900 dark:text-white font-medium text-left">Organizational capabilities</span>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-900 dark:text-white font-medium text-left">Governance and compliance readiness</span>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-900 dark:text-white font-medium text-left">AI/ML implementation maturity</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results step
  if (currentStep === 'results' && assessmentResults) {
    // Get assessment ID from results (prefer assessment_id, fallback to id)
    const assessmentId = assessmentResults.assessment_id || assessmentResults.id || currentAssessmentIdFromResults || submittedAssessmentId;
    
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        <AssessmentResults
          assessmentResults={assessmentResults}
          onDownloadPDF={() => handlePDFDownload(assessmentId || '')}
          pdfDownloading={pdfDownloading}
          onBookSME={handleBookSME}
          onNewAssessment={handleNewAssessment}
          onBackToAssessments={handleBackToAssessments}
          onShare={handleGenerateShareLink}
          shareLoading={shareLinkLoading}
        />
        
        {/* SME Booking Modal */}
        <SMEBookingModal
          isOpen={showSMEBooking}
          onClose={() => setShowSMEBooking(false)}
          timeSlots={generateTimeSlots()}
          selectedTimeSlot={selectedTimeSlot}
          onSelectTimeSlot={(slot) => setSelectedTimeSlot(slot as never)}
          onBookSession={(data) => handleSMESubmit(data as unknown as Record<string, unknown>)}
          bookingLoading={bookingLoading}
        />

        {/* Contact/SME Modal */}
        {showContactModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 transition-colors">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white transition-colors">Review with an SME</h2>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                {contactSubmitted ? (
                  <div className="text-center">
                    <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 transition-colors">Request Submitted!</h3>
                    <p className="text-gray-600 dark:text-gray-400 transition-colors">
                      Thank you for your interest! An AI specialist will review your assessment and contact you within 24 hours.
                    </p>
                    <button
                      onClick={() => setShowContactModal(false)}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-700 dark:text-gray-300 mb-6 transition-colors">
                      Connect with Aivilo Data and AI experts to review your report and discuss personalized recommendations for your AI transformation journey.
                    </p>
                    {isAnonymousUser && (
                      <div className="mb-4">
                        <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                          Your email address
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        />
                      </div>
                    )}
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setShowContactModal(false)}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleContactSubmit}
                        disabled={contactSubmitting || (isAnonymousUser && !contactEmail)}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                      >
                        {contactSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>Submitting...</span>
                          </>
                        ) : (
                          <span>Contact Me</span>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Share Link Modal */}
        <ShareLinkModal
          show={showShareLinkModal}
          shareLinkData={shareLinkData}
          shareLinkError={shareLinkError}
          linkCopied={linkCopied}
          onClose={() => { setShowShareLinkModal(false); setShareLinkData(null); setShareLinkError(null); }}
          onCopy={handleCopyShareLink}
        />
      </div>
    );
  }

  // Assessments timeline step
  if (currentStep === 'assessments') {
    // Separate completed vs in-progress assessments
    const completedAssessments = assessmentsList.filter(a =>
      (['completed', 'success'].includes(a.status))
    );
    const processingAssessments = assessmentsList.filter(a => a.status === 'processing');
    const draftAssessment = assessmentsList.find(a =>
      a.is_draft || a.status === 'draft'
    );

    // Timeline entries: processing first, then completed (both sorted by created_at desc)
    const timelineEntries = [
      ...processingAssessments.map(a => ({ ...a, _timelineType: 'processing' })),
      ...completedAssessments.map(a => ({ ...a, _timelineType: 'completed' })),
    ];

    // Calculate score delta between consecutive completed assessments
    const getScoreDelta = (index: number): number | null => {
      if (index >= completedAssessments.length - 1) return null;
      const current = completedAssessments[index].score;
      const previous = completedAssessments[index + 1].score;
      if (current == null || previous == null) return null;
      return current - previous;
    };

    const getVersionLabel = (index: number): string => {
      if (index === 0) return 'Latest Assessment';
      return `v${completedAssessments.length - index}`;
    };

    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white transition-colors">Your AI Strategy Journey</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400 transition-colors">
            Track your progress and take new assessments to measure improvement
          </p>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
          <button
            onClick={handleNewAssessment}
            disabled={!canCreateAssessment || loading}
            className={`px-4 py-2 rounded-md transition-colors text-sm sm:text-base ${
              !canCreateAssessment || loading
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            title={canCreateAssessment ? '' : 'You do not have permission to create assessments'}
          >
            {loading ? 'Creating...' : 'Retake Assessment'}
          </button>
          <button
            onClick={loadAssessments}
            disabled={assessmentsLoading}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 transition-colors text-sm sm:text-base"
          >
            {assessmentsLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {assessmentsLoading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading assessments...</p>
          </div>
        )}
        {!assessmentsLoading && assessmentsList.length === 0 && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center transition-colors">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 transition-colors">No assessments yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Take your first AI strategy assessment to get started.</p>
          </div>
        )}
        {!assessmentsLoading && assessmentsList.length > 0 && (
          <div className="space-y-6">
            {/* Progress Summary Card */}
            {completedAssessments.length >= 2 && completedAssessments[0].score != null && (
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Latest Score</p>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{completedAssessments[0].score}<span className="text-lg text-gray-400">/100</span></p>
                  </div>
                  <div className="sm:text-right">
                    {(() => {
                      const delta = getScoreDelta(0);
                      if (delta === null) return null;
                      return (
                        <div className={`flex items-center space-x-1 ${delta >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          <TrendingUp className={`w-5 h-5 ${delta < 0 ? 'transform rotate-180' : ''}`} />
                          <span className="text-base sm:text-lg font-semibold">{delta >= 0 ? '+' : ''}{delta} points</span>
                        </div>
                      );
                    })()}
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{completedAssessments.length} assessments completed</p>
                  </div>
                </div>
              </div>
            )}

            {/* Draft Banner */}
            {draftAssessment && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 border-2 border-blue-600 dark:border-blue-400 rounded-full flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-100">Draft in Progress</p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Started {new Date(draftAssessment.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 self-end sm:self-auto">
                  <button
                    onClick={async () => {
                      if (globalThis.confirm('Are you sure you want to delete this draft?')) {
                        const result = await assessmentAPI.deleteDraftAssessment(draftAssessment.id);
                        if (result.success) {
                          await loadAssessments();
                        }
                      }
                    }}
                    className="px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors text-sm"
                    title="Delete draft"
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      globalThis.location.href = `/hub/assessments?assessment_id=${draftAssessment.id}`;
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Timeline */}
            {timelineEntries.length > 0 && (
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6 transition-colors">Assessment Timeline</h3>
                <div className="relative">
                  {/* Vertical line */}
                  {timelineEntries.length > 1 && (
                    <div className="absolute left-5 top-6 bottom-6 w-0.5 bg-gray-200 dark:bg-gray-700" />
                  )}

                  <div className="space-y-6">
                    {timelineEntries.map((assessment, _index) => {
                      const isProcessing = assessment._timelineType === 'processing';
                      // For completed entries, find their index among completed assessments for delta/version
                      const completedIndex = isProcessing ? -1 : completedAssessments.indexOf(completedAssessments.find(a => a.id === assessment.id) as AssessmentListItem);
                      const delta = isProcessing ? null : getScoreDelta(completedIndex);
                      const versionLabel = isProcessing ? 'Processing' : getVersionLabel(completedIndex);
                      const isExpanded = expandedTimelineId === assessment.id;
                      const currentScores = timelineResults[assessment.id];
                      // Previous assessment for category deltas
                      const prevAssessment = !isProcessing && completedIndex < completedAssessments.length - 1
                        ? completedAssessments[completedIndex + 1]
                        : null;
                      const prevScores = prevAssessment ? timelineResults[prevAssessment.id] : null;

                      return (
                        <div key={assessment.id} className="relative">
                          <div className="flex items-start space-x-4">
                            {/* Timeline dot */}
                            {isProcessing ? (
                              <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-500 text-white">
                                <Clock className="w-5 h-5 animate-spin" />
                              </div>
                            ) : (
                              <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                                completedIndex === 0 && processingAssessments.length === 0
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                              }`}>
                                <CheckCircle className="w-5 h-5" />
                              </div>
                            )}

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div
                                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${isProcessing ? '' : 'cursor-pointer'}`}
                                onClick={isProcessing ? undefined : async () => {
                                  if (isExpanded) {
                                    setExpandedTimelineId(null);
                                  } else {
                                    setExpandedTimelineId(assessment.id);
                                    // Fetch current assessment results if not cached
                                    await fetchTimelineCategoryScores(assessment.id);
                                    // Also fetch previous assessment results for delta computation
                                    if (prevAssessment && !timelineResults[prevAssessment.id]) {
                                      fetchTimelineCategoryScores(prevAssessment.id);
                                    }
                                  }
                                }}
                                onKeyDown={isProcessing ? undefined : async (e) => { if (e.key === 'Enter' || e.key === ' ') { if (isExpanded) { setExpandedTimelineId(null); } else { setExpandedTimelineId(assessment.id); await fetchTimelineCategoryScores(assessment.id); if (prevAssessment && !timelineResults[prevAssessment.id]) { fetchTimelineCategoryScores(prevAssessment.id); } } } }}
                                role={isProcessing ? undefined : "button"}
                                tabIndex={isProcessing ? undefined : 0}
                              >
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <span className={`text-sm font-medium transition-colors ${isProcessing ? 'text-amber-700 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
                                      {versionLabel}
                                    </span>
                                    {delta != null && (
                                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                        delta >= 0
                                          ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                          : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                                      }`}>
                                        {delta >= 0 ? '+' : ''}{delta}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                    {new Date(assessment.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                  </p>
                                </div>
                                <div className="flex items-center space-x-2 sm:space-x-4">
                                  {isProcessing ? (
                                    <button
                                      onClick={() => {
                                        setSubmittedAssessmentId(assessment.id);
                                        setCurrentStep('processing');
                                      }}
                                      className="px-3 py-1.5 bg-amber-500 text-white text-sm rounded-md hover:bg-amber-600 transition-colors"
                                    >
                                      Check Status
                                    </button>
                                  ) : (
                                    <>
                                      {assessment.score != null && (
                                        <div className="text-right">
                                          <span className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{assessment.score}</span>
                                          <span className="text-sm text-gray-400">/100</span>
                                        </div>
                                      )}
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          await checkAssessmentResults(assessment.id);
                                          setCurrentAssessmentIdFromResults(assessment.id);
                                        }}
                                        className="px-2 sm:px-3 py-1.5 bg-blue-600 text-white text-xs sm:text-sm rounded-md hover:bg-blue-700 transition-colors"
                                      >
                                        View
                                      </button>
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (isExpanded) {
                                            setExpandedTimelineId(null);
                                          } else {
                                            setExpandedTimelineId(assessment.id);
                                            await fetchTimelineCategoryScores(assessment.id);
                                            if (prevAssessment && !timelineResults[prevAssessment.id]) {
                                              fetchTimelineCategoryScores(prevAssessment.id);
                                            }
                                          }
                                        }}
                                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                        title={isExpanded ? 'Collapse category scores' : 'Expand category scores'}
                                      >
                                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Expanded category breakdown */}
                              {isExpanded && !isProcessing && (
                                <div className="mt-4 ml-1 border-t border-gray-100 dark:border-gray-700 pt-4">
                                  {timelineResultsLoading === assessment.id && (
                                    <div className="flex items-center space-x-2 py-2">
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                                      <span className="text-sm text-gray-500 dark:text-gray-400">Loading category scores...</span>
                                    </div>
                                  )}
                                  {timelineResultsLoading !== assessment.id && currentScores && Object.keys(currentScores).length > 0 && (
                                    <div className="space-y-3">
                                      {Object.entries(currentScores).map(([category, score]) => {
                                        const prevScore = prevScores ? prevScores[category] : null;
                                        const catDelta = prevScore == null ? null : score - prevScore;
                                        return (
                                          <div key={category} className="flex items-center space-x-3">
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                                  {formatCategoryName(category)}
                                                </span>
                                                <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                                  <span className="text-xs font-semibold text-gray-900 dark:text-white">{score}</span>
                                                  {catDelta != null && catDelta !== 0 && (
                                                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                                                      catDelta > 0
                                                        ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                                        : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                                                    }`}>
                                                      {catDelta > 0 ? '+' : ''}{catDelta}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                                <div
                                                  className="bg-blue-600 h-1.5 rounded-full transition-all"
                                                  style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {timelineResultsLoading !== assessment.id && !(currentScores && Object.keys(currentScores).length > 0) && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No category scores available.</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Failed assessments */}
            {assessmentsList.filter(a => a.status === 'failed').map(assessment => (
              <div key={assessment.id} className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 transition-colors">
                <div>
                  <p className="font-medium text-red-900 dark:text-red-200">Assessment Failed</p>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {new Date(assessment.created_at).toLocaleDateString()} - Processing error occurred
                  </p>
                </div>
                <button
                  onClick={() => {
                    globalThis.location.href = `/hub/assessments?assessment_id=${assessment.id}`;
                  }}
                  className="px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 text-sm rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors self-end sm:self-auto"
                >
                  Retry
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default AssessmentPage;
